import type { Plan } from "@/components/subscribe/PlanSelector";
import {
  promotionForPlan,
  tierWithOffer,
  type PublicPriceOffer,
} from "@/lib/pricing/offer";
import {
  PLAN_TIERS,
  formatHkd,
  type BillingInterval,
  type PlanTier,
  getTierPriceLabel,
  type PlanPriceLabels,
} from "@/lib/subscription/planCatalog";

export type { BillingInterval, PlanTier };

/** Loose translator shape so next-intl `useTranslations("Plans")` assigns cleanly. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TranslatePlans = (key: any, values?: any) => string;

function priceLabelsFromT(t: TranslatePlans): PlanPriceLabels {
  return {
    custom: t("custom"),
    periodMo: t("periodMo"),
    periodYr: t("periodYr"),
    annualDiscount: t("annualDiscount"),
    yearlySublabel: (monthly, discount) =>
      t("yearlySublabel", { monthly, discount }),
  };
}

function localizeNumberLocale(locale: string): string {
  if (locale === "zh-CN") return "zh-CN";
  if (locale === "zh-HK") return "zh-HK";
  return "en-HK";
}

export function buildSubscriptionPlans(
  interval: BillingInterval,
  t: TranslatePlans,
  locale = "en",
  offer?: PublicPriceOffer
): Plan[] {
  const labels = priceLabelsFromT(t);
  const numberLocale = localizeNumberLocale(locale);

  return PLAN_TIERS.map((tier) => {
    const priced = offer ? tierWithOffer(tier, offer) : tier;
    const { price, period, sublabel } = getTierPriceLabel(
      priced,
      interval,
      labels,
      numberLocale
    );
    const promo = offer
      ? promotionForPlan(offer, tier.id, interval, locale)
      : null;
    return {
      id: tier.id,
      name: t(`${tier.id}.name`),
      price,
      period,
      priceSublabel: sublabel,
      compareAtPrice: promo
        ? formatHkd(promo.compareAtHkd, numberLocale)
        : undefined,
      promotionText: promo?.text,
      description: t(`${tier.id}.description`),
      features: tier.featureKeys.map((key) => t(`${tier.id}.features.${key}`)),
      highlighted: tier.highlighted,
    };
  });
}
